import { stripHtmlToPlainText } from './stripHtmlToPlainText';

/**
 * True if line looks like a GFM table separator ( |---|---| ).
 */
function isMarkdownTableSeparatorRow(line) {
  const t = line.trim();
  if (!t.includes('|')) return false;
  const inner = t
    .split('|')
    .map((c) => c.trim())
    .filter((c) => c.length > 0);
  if (inner.length < 2) return false;
  return inner.every((c) => /^[\s\-:]+$/.test(c));
}

function parsePipeRowCells(line) {
  const t = line.trim();
  if (!t.includes('|')) return null;
  const parts = t.split('|');
  if (parts.length < 3) return null;
  const inner = parts.slice(1, -1).map((c) => c.trim());
  return inner.length ? inner : null;
}

/**
 * If lines[start..] begin a pipe table (header + separator + ≥1 data row), return parsed structure.
 */
export function tryParseMarkdownPipeTable(lines, start) {
  if (start >= lines.length) return null;
  const headerCells = parsePipeRowCells(lines[start]);
  if (!headerCells || headerCells.length < 2) return null;
  if (start + 1 >= lines.length) return null;
  if (!isMarkdownTableSeparatorRow(lines[start + 1])) return null;

  const dataRows = [];
  let i = start + 2;
  while (i < lines.length) {
    const raw = lines[i];
    const trimmed = raw.trim();
    if (!trimmed.startsWith('|') || !trimmed.includes('|')) break;
    const cells = parsePipeRowCells(raw);
    if (!cells || cells.length === 0) break;
    dataRows.push(cells);
    i += 1;
  }

  if (dataRows.length === 0) return null;

  return { headerCells, dataRows, endExclusive: i };
}

/**
 * Render one data row as labeled lines using header names.
 */
function renderRowAsLabeledBlocks(headerCells, cellValues) {
  const n = headerCells.length;
  const out = [];
  for (let j = 0; j < n; j += 1) {
    const h = headerCells[j] || `Column ${j + 1}`;
    const v = cellValues[j] != null ? cellValues[j] : '';
    out.push(`${h}: ${v}`);
  }
  return out;
}

/**
 * Convert GitHub-style Markdown pipe tables to plain labeled blocks (no | pipes).
 */
export function flattenMarkdownPipeTables(text) {
  if (text == null || text === '') return '';
  const lines = String(text).split(/\r?\n/);
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const parsed = tryParseMarkdownPipeTable(lines, i);
    if (parsed) {
      const { headerCells, dataRows, endExclusive } = parsed;
      dataRows.forEach((row, rowIdx) => {
        if (rowIdx > 0) out.push('');
        const padded = [...row];
        while (padded.length < headerCells.length) padded.push('');
        out.push(...renderRowAsLabeledBlocks(headerCells, padded.slice(0, headerCells.length)));
      });
      i = endExclusive;
      continue;
    }
    out.push(lines[i]);
    i += 1;
  }
  return out.join('\n');
}

/**
 * Normalize text for template-language fields: strip HTML, flatten Markdown tables to plain labels.
 * Use after LLM and ADK steps so protocol text never shows raw `|` tables in TextFields.
 */
export function normalizeTemplateLanguagePlainText(value) {
  let s = stripHtmlToPlainText(value, { preserveInteriorSpaces: true });
  s = flattenMarkdownPipeTables(s);
  s = s.replace(/\n{3,}/g, '\n\n');
  return s.trim();
}
